import type { IDateOfBirthValue } from './types';

const DAYS_IN_MONTH: readonly number[] = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

const isLeapYear = (year: number): boolean => (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

/**
 * Days in a 1-indexed `month`, with February yielding 29 in a leap year.
 * A `year` of 0 (the "year not yet picked" sentinel) reads as a leap year, so
 * Feb stays permissive at 29 until a real year is chosen. Assumes `month` is
 * in 1..12 — callers must guard out-of-range months themselves.
 */
export const daysInMonth = (month: number, year: number): number =>
  month === 2 && isLeapYear(year) ? 29 : DAYS_IN_MONTH[month - 1];

/**
 * Validates a Month/Day/Year triad. The triad is structurally a real calendar
 * date — leap-year Feb 29 is accepted only when the year actually is a leap
 * year, and month-overflowing days (Apr 31, etc.) are rejected. A missing part
 * is rejected too: the input uses `0` as the "unset" sentinel, so a year (or
 * month/day) of 0 is not a valid date.
 */
export const isValidDateOfBirth = (dob: IDateOfBirthValue): boolean => {
  const { month, day, year } = dob;
  if (year < 1 || month < 1 || month > 12 || day < 1 || day > 31) {
    return false;
  }
  return day <= daysInMonth(month, year);
};
